package com.shadowings.gero.data.lessons.chapter12

import com.shadowings.gero.data.lessons.buildAdverbialSentenceSync
import com.shadowings.gero.data.lessons.buildAdverbialTextTranslationQuizzes
import com.shadowings.gero.data.lessons.buildVsoRecapWordSelectionQuizzes
import com.shadowings.gero.data.lessons.chapter11.buildRichestPseudoverbalSentence
import com.shadowings.gero.data.lessons.chapter11.buildWordBankFromWords
import com.shadowings.gero.data.repository.DatabaseRepositoryImpl
import com.shadowings.gero.data.repository.SentenceOptions
import com.shadowings.gero.domain.model.Characters
import com.shadowings.gero.domain.model.ExplanationContent
import com.shadowings.gero.domain.model.Faces
import com.shadowings.gero.domain.model.IExplanation
import com.shadowings.gero.domain.model.IQuizContent
import com.shadowings.gero.domain.model.PhoneticKeyboardContent
import com.shadowings.gero.domain.model.TextTranslationContent
import com.shadowings.gero.domain.model.WordSelectionContent
import com.shadowings.gero.presentation.model.Verb
import com.shadowings.gero.presentation.model.Word
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.exp_content_torniamo_alla_grammatica_1
import gero.composeapp.generated.resources.exp_content_torniamo_alla_grammatica_2
import gero.composeapp.generated.resources.exp_content_torniamo_alla_grammatica_3
import gero.composeapp.generated.resources.exp_title_torniamo_alla_grammatica
import kotlin.random.Random

fun getTorniamoAllaGrammaticaContent(): List<IExplanation> {
    return listOf(
        ExplanationContent(
            title = Res.string.exp_title_torniamo_alla_grammatica,
            content = Res.string.exp_content_torniamo_alla_grammatica_1,
            face = Faces.Happy,
            character = Characters.Alyn,
        ),
        ExplanationContent(
            title = Res.string.exp_title_torniamo_alla_grammatica,
            content = Res.string.exp_content_torniamo_alla_grammatica_2,
            face = Faces.Idle,
            character = Characters.Alyn,
        ),
        ExplanationContent(
            title = Res.string.exp_title_torniamo_alla_grammatica,
            content = Res.string.exp_content_torniamo_alla_grammatica_3,
            face = Faces.Happy,
            character = Characters.Alyn,
        ),
    )
}

// Same 2 VSO + 2 adverbial + 6 pseudoverbal shape as chapter 11's
// getRecapFrasiPseudoverbaliQuizContent, but the pseudoverbal half doesn't
// commit upfront to one construction. Forcing a genitive-marked complex
// object is correct for COMPLEMENTI_COMPLESSI, which is teaching exactly
// that construction - but this is meant to be the final "everything you've
// learned" recap, and several verbs (cantare, parlare, esistere, volare,
// dire, entrare, uscire, fare...) have no object at all in
// sentences_verbial.tsv, so forcing one here would silently exclude them
// from this lesson forever. Not forcing it isn't the same as never using
// it, though: richestPseudoverbalSentence below picks a random row first
// and then uses the richest construction THAT row actually supports (object
// + possessor, just object, or bare subject + verb), so a row that does
// have both still gets to show them off. Chapter 11's own
// RECAP_FRASI_PSEUDOVERBALI and LA_PAZIENZA_E_FINITA keep using the
// complex-object version unchanged - they're deliberately testing that
// specific construction, not "everything".
fun getTorniamoAllaGrammaticaQuizContent(
    verbs: List<Verb>,
    words: List<Word>,
    languageCode: String,
    random: Random = Random,
    verbSentences: List<SentenceOptions> = DatabaseRepositoryImpl.verbSentences
): List<IQuizContent> {
    // 1) 2 Frasi VSO: 1 WordBank + 1 TextTranslation
    var vsoQuizzes = buildVsoRecapWordSelectionQuizzes(
        random = random,
        words = words,
        verbs = verbs,
        languageCode = languageCode,
        count = 2,
        verbSentences = verbSentences
    )
    if (vsoQuizzes.size < 2) {
        vsoQuizzes = buildVsoRecapWordSelectionQuizzes(
            random = random,
            words = words,
            verbs = verbs,
            languageCode = languageCode,
            count = 2
        )
    }
    val vsoWordBank = vsoQuizzes[0] as WordSelectionContent
    val vso2Wb = vsoQuizzes[1] as WordSelectionContent
    val vsoTextTrans = TextTranslationContent(
        hieroglyphs = vso2Wb.hieroglyphs,
        correctPhrase = vso2Wb.correctPhrase,
        transliteration = vso2Wb.transliteration,
        id = vso2Wb.id
    )

    // 2) 2 Frasi avverbiali: 1 WordBank + 1 TextTranslation
    val adv1 = buildAdverbialSentenceSync(
        soggettiCount = 1,
        withParticleIw = true,
        random = random,
        words = words,
    )
    val adv1Translation = when (languageCode) {
        "it" -> adv1.translationIt
        "de" -> adv1.translationDe
        "es" -> adv1.translationEs
        "fr" -> adv1.translationFr
        else -> adv1.translationEn
    }
    val advWordBank = WordSelectionContent(
        hieroglyphs = adv1.hieroglyphs,
        correctPhrase = adv1Translation,
        words = buildWordBankFromWords(adv1Translation, words, verbs, languageCode, random),
        transliteration = adv1.transliteration,
        soggettiCount = 1
    )
    val advTextTrans = buildAdverbialTextTranslationQuizzes(
        random = random,
        words = words,
        languageCode = languageCode,
        count = 1,
        withParticleIw = true
    ).first()

    // 3) 6 Frasi pseudoverbali: 2 WordBank + 2 TextTranslation + 2 PhoneticKeyboard.
    // buildRichestPseudoverbalSentence picks a random row, then the richest
    // construction THAT row supports - object + possessor, just an object, or bare
    // subject + verb - instead of committing to one construction for every pick
    // regardless of what's actually available.
    val pool = verbSentences.filter { it.verbs.isNotEmpty() }
        .ifEmpty { DatabaseRepositoryImpl.verbSentences.filter { it.verbs.isNotEmpty() } }

    fun richestPseudoverbalSentence(isPronominal: Boolean) =
        buildRichestPseudoverbalSentence(pool, verbs, words, isPronominal, languageCode, random)

    val (pseudo1Hiero, pseudo1Translit, pseudo1Trans) = richestPseudoverbalSentence(isPronominal = true)
    val pseudoWordBank1 = WordSelectionContent(
        hieroglyphs = pseudo1Hiero,
        correctPhrase = pseudo1Trans,
        words = buildWordBankFromWords(pseudo1Trans, words, verbs, languageCode, random),
        transliteration = pseudo1Translit
    )

    val (pseudo2Hiero, pseudo2Translit, pseudo2Trans) = richestPseudoverbalSentence(isPronominal = false)
    val pseudoWordBank2 = WordSelectionContent(
        hieroglyphs = pseudo2Hiero,
        correctPhrase = pseudo2Trans,
        words = buildWordBankFromWords(pseudo2Trans, words, verbs, languageCode, random),
        transliteration = pseudo2Translit
    )

    val (pseudo3Hiero, pseudo3Translit, pseudo3Trans) = richestPseudoverbalSentence(isPronominal = true)
    val pseudoTextTrans1 = TextTranslationContent(
        hieroglyphs = pseudo3Hiero,
        correctPhrase = pseudo3Trans,
        transliteration = pseudo3Translit
    )

    val (pseudo4Hiero, pseudo4Translit, pseudo4Trans) = richestPseudoverbalSentence(isPronominal = false)
    val pseudoTextTrans2 = TextTranslationContent(
        hieroglyphs = pseudo4Hiero,
        correctPhrase = pseudo4Trans,
        transliteration = pseudo4Translit
    )

    val (pseudo5Hiero, pseudo5Translit, pseudo5Trans) = richestPseudoverbalSentence(isPronominal = true)
    val pseudoKeyboard1 = PhoneticKeyboardContent(
        sentence = pseudo5Hiero,
        info = "$pseudo5Translit -> ${pseudo5Trans}",
        reading = pseudo5Translit.map { it.toString() },
        isProperName = false,
        canRotate = false
    )

    val (pseudo6Hiero, pseudo6Translit, pseudo6Trans) = richestPseudoverbalSentence(isPronominal = false)
    val pseudoKeyboard2 = PhoneticKeyboardContent(
        sentence = pseudo6Hiero,
        info = "$pseudo6Translit -> ${pseudo6Trans}",
        reading = pseudo6Translit.map { it.toString() },
        isProperName = false,
        canRotate = false
    )

    return listOf(
        vsoWordBank,
        vsoTextTrans,
        advWordBank,
        advTextTrans,
        pseudoWordBank1,
        pseudoWordBank2,
        pseudoTextTrans1,
        pseudoTextTrans2,
        pseudoKeyboard1,
        pseudoKeyboard2
    ).shuffled(random)
}
